import type {
  ModuleResult,
  RepairHit,
  RepairLanguage,
  RepairSearchRequest,
  RepairSearchResponse,
  Vehicle,
  VehiclesResponse,
} from "@models/repairInformation";
import { getJson, postJson } from "./apiClient";

type Wire = Record<string, unknown>;

function vehicleFromWire(v: Wire): Vehicle {
  return {
    typeId: v.type_id as number,
    bodyId: v.body_id as number,
    countryCode: (v.country_code as string) ?? "DE",
    ktype: (v.ktype as number | null) ?? null,
    make: (v.make as string | null) ?? null,
    model: (v.model as string | null) ?? null,
    typeName: (v.type_name as string | null) ?? null,
    languages: ((v.languages as RepairLanguage[]) ?? []).filter(Boolean),
  };
}

function hitFromWire(h: Wire): RepairHit {
  return {
    key: h.key as string,
    postingKey: h.posting_key as string,
    module: h.module as RepairHit["module"],
    title: h.title as string,
    breadcrumb: (h.breadcrumb as string[]) ?? [],
    facts: (h.facts as RepairHit["facts"]) ?? [],
    source: (h.source as Record<string, unknown>) ?? {},
    score: h.score as number,
    semanticScore: h.semantic_score as number,
    lexicalScore: h.lexical_score as number,
    titleScore: h.title_score as number,
    routeSignal: (h.route_signal as number) ?? 0,
  };
}

function requestToWire(r: RepairSearchRequest): Wire {
  return {
    query: r.query,
    vehicle: { type_id: r.vehicle.typeId, body_id: r.vehicle.bodyId, country_code: r.vehicle.countryCode },
    language: r.language,
    query_language: r.queryLanguage,
    module: r.module ?? undefined,
    threshold: r.threshold,
  };
}

class RepairInformationApi {
  async listVehicles(params: { make?: string; q?: string } = {}): Promise<VehiclesResponse> {
    const data = await getJson<{ dataset: Wire; vehicles?: Wire[] }>(
      "/repair-information/vehicles",
      params,
    );
    return {
      dataset: {
        id: data.dataset.id as string,
        contentHash: (data.dataset.content_hash as string | null) ?? null,
        updatedAt: (data.dataset.updated_at as string | null) ?? null,
      },
      vehicles: (data.vehicles ?? []).map(vehicleFromWire),
    };
  }

  async search(request: RepairSearchRequest): Promise<RepairSearchResponse> {
    const data = await postJson<Wire>("/repair-information/search", requestToWire(request));
    return {
      status: data.status as RepairSearchResponse["status"],
      vehicle: (data.vehicle as Record<string, unknown>) ?? {},
      language: data.language as RepairLanguage,
      selectedModule: (data.selected_module as RepairSearchResponse["selectedModule"]) ?? null,
      answer: (data.answer as string | null) ?? null,
      citations: (data.citations as string[]) ?? [],
      hits: ((data.hits as Wire[]) ?? []).map(hitFromWire),
      moduleResults: ((data.module_results as ModuleResult[]) ?? []),
      degraded: (data.degraded as string[]) ?? [],
    };
  }
}

export const repairInformationApi = new RepairInformationApi();
